import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { getStudent, updateStudent, logPayment, deleteStudent } from '../../lib/api'
import { balanceLabel } from '../../lib/balance'
import { keys } from '../../lib/queryKeys'
import { StatusBadge } from '../../components/StatusBadge'
import { Entry, EntryList } from '../../components/Entry'
import { prettyPhone } from '../../lib/phone'
import { m } from '../../paraglide/messages'
import type { Student, Lesson, Payment } from '../../types'

export const Route = createFileRoute('/students/$id')({
  component: StudentDetailPage,
})

const fmtDate = new Intl.DateTimeFormat('he-IL', { dateStyle: 'short', timeZone: 'Asia/Jerusalem' })
function fd(iso: string | null | undefined) {
  if (!iso) return '—'
  return fmtDate.format(new Date(iso))
}

const paymentMethodLabel: Record<string, () => string> = {
  cash:   () => m.method_cash(),
  bank:   () => m.method_bank(),
  bit:    () => m.method_bit(),
  paybox: () => m.method_paybox(),
  other:  () => m.method_other(),
}

function StudentDetailPage() {
  const { id }      = Route.useParams()
  const navigate    = useNavigate()
  const queryClient = useQueryClient()

  const { data: student, isLoading, error } = useQuery<Student>({
    queryKey: keys.student(id),
    queryFn:  () => getStudent(id),
  })

  const remove = useMutation({
    mutationFn: () => deleteStudent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.students })
      navigate({ to: '/students' })
    },
  })

  if (isLoading) return <p className="text-zinc-400">{m.loading()}</p>
  if (error || !student) return <p className="text-red-600 text-sm">{(error as Error)?.message ?? m.not_found()}</p>

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Link to="/students" className="text-xs text-zinc-400 hover:text-zinc-900 mb-2 inline-block">
          {m.student_back()}
        </Link>
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-base font-semibold text-zinc-900">{student.name}</h1>
          <button
            onClick={() => {
              if (confirm(m.student_delete_confirm({ name: student.name }))) {
                remove.mutate()
              }
            }}
            disabled={remove.isPending}
            className="border border-zinc-300 text-zinc-500 px-3 py-1.5 text-sm hover:border-red-600 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
          >
            {remove.isPending ? m.student_deleting() : m.student_delete()}
          </button>
        </div>
      </div>

      <InfoSection student={student} onSaved={() => {
        queryClient.invalidateQueries({ queryKey: keys.student(id) })
        queryClient.invalidateQueries({ queryKey: keys.students })
      }} />

      <BalanceSection
        student={student}
        onSaved={() => {
          // Balance is computed on the fly — refresh both the detail view and the
          // Students list so neither shows a stale value after a payment (#30).
          queryClient.invalidateQueries({ queryKey: keys.student(id) })
          queryClient.invalidateQueries({ queryKey: keys.students })
        }}
      />

      <LessonsSection lessons={student.lessons ?? []} />

      <PaymentsSection payments={student.payments ?? []} />
    </div>
  )
}

// ─── Info Section ─────────────────────────────────────────────────────────────

function InfoSection({ student, onSaved }: { student: Student; onSaved: () => void }) {
  const [editing, setEditing] = useState(false)
  const [name, setName]       = useState(student.name)
  const [phone, setPhone]     = useState(student.whatsappPhone ?? '')
  const [email, setEmail]     = useState(student.email ?? '')
  const [rate, setRate]       = useState(student.rate)
  const [notes, setNotes]     = useState(student.notes ?? '')
  const [error, setError]     = useState('')

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => updateStudent(student.id, body),
    onSuccess: () => {
      onSaved()
      setEditing(false)
      setError('')
    },
    onError: (err) => setError((err as Error).message),
  })

  const handleSave = () => {
    mutation.mutate({ name, phone, email, rate, notes })
  }

  const handleCancel = () => {
    setName(student.name)
    setPhone(student.whatsappPhone ?? '')
    setEmail(student.email ?? '')
    setRate(student.rate)
    setNotes(student.notes ?? '')
    setError('')
    setEditing(false)
  }

  const fieldCls = 'border border-zinc-300 px-2 py-1.5 text-sm w-full focus:outline-none focus:border-zinc-900'

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-zinc-900">{m.student_info()}</h2>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="border border-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-100"
          >
            {m.action_edit()}
          </button>
        )}
      </div>

      {editing ? (
        <div className="flex flex-col gap-3 max-w-lg">
          <Field label={m.form_name()}>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={fieldCls} />
          </Field>
          <Field label={m.form_phone()}>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="972501234567"
              className={fieldCls}
            />
          </Field>
          <Field label={m.form_email()}>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={fieldCls} />
          </Field>
          <Field label={m.form_rate()}>
            <input type="number" min="0" step="any" value={rate} onChange={(e) => setRate(e.target.value)} className={fieldCls} />
          </Field>
          <Field label={m.form_notes()}>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className={fieldCls} />
          </Field>

          {error && <p className="text-red-600 text-sm">{error}</p>}

          <div className="flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={mutation.isPending}
              className="bg-zinc-900 text-white px-3 py-1.5 text-sm hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {mutation.isPending ? m.saving() : m.action_save()}
            </button>
            <button
              onClick={handleCancel}
              className="border border-zinc-900 px-3 py-1.5 text-sm hover:bg-zinc-100"
            >
              {m.action_cancel()}
            </button>
          </div>
        </div>
      ) : (
        <table className="text-sm border-collapse w-full max-w-lg">
          <tbody>
            <ReadRow label={m.form_phone()} value={prettyPhone(student.whatsappPhone)} />
            <ReadRow label={m.form_email()} value={student.email ?? '—'} />
            <ReadRow label={m.form_rate()}  value={m.rate_per_hour({ amount: Number(student.rate).toFixed(0) })} />
            <tr className="border-b border-zinc-100">
              <td className="py-2 pr-6 text-xs text-zinc-400 uppercase tracking-wide align-top pt-3">{m.form_notes()}</td>
              <td className="py-2">
                <span className="text-sm text-zinc-900 whitespace-pre-wrap">{student.notes || '—'}</span>
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </section>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-sm text-zinc-700">{label}</span>
      {children}
    </label>
  )
}

function ReadRow({ label, value }: { label: string; value: string }) {
  return (
    <tr className="border-b border-zinc-100">
      <td className="py-2 pr-6 text-xs text-zinc-400 uppercase tracking-wide w-32">{label}</td>
      <td className="py-2 text-sm text-zinc-900">{value}</td>
    </tr>
  )
}

// ─── Balance Section ──────────────────────────────────────────────────────────

function BalanceSection({ student, onSaved }: { student: Student; onSaved: () => void }) {
  const [amount, setAmount]   = useState('')
  const [method, setMethod]   = useState('cash')
  const [note, setNote]       = useState('')
  const [error, setError]     = useState('')

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => logPayment(student.id, body),
    onSuccess: () => {
      onSaved()
      setAmount('')
      setNote('')
      setError('')
    },
    onError: (err) => setError((err as Error).message),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!amount) return
    mutation.mutate({ amount, method, note: note || undefined })
  }

  const balance = Number(student.balance)

  return (
    <section className="mb-8">
      <h2 className="text-sm font-semibold text-zinc-900 mb-3">{m.student_balance()}</h2>
      <p className="text-sm mb-4 text-zinc-900">{balanceLabel(balance)}</p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3 max-w-md">
        <p className="text-xs text-zinc-400 uppercase tracking-wide">{m.student_log_payment()}</p>
        <div className="flex gap-3">
          <div className="flex flex-col gap-1 flex-1">
            <label className="text-sm text-zinc-700" htmlFor="pay-amount">{m.student_amount()}</label>
            <input
              id="pay-amount"
              type="number"
              step="any"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              className="border border-zinc-300 px-2 py-1.5 text-sm w-full focus:outline-none focus:border-zinc-900"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm text-zinc-700" htmlFor="pay-method">{m.student_method()}</label>
            <select
              id="pay-method"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="border border-zinc-300 px-2 py-1.5 text-sm focus:outline-none focus:border-zinc-900"
            >
              <option value="cash">{m.method_cash()}</option>
              <option value="bank">{m.method_bank()}</option>
              <option value="bit">{m.method_bit()}</option>
              <option value="paybox">{m.method_paybox()}</option>
              <option value="other">{m.method_other()}</option>
            </select>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-sm text-zinc-700" htmlFor="pay-note">{m.student_note()}</label>
          <input
            id="pay-note"
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="border border-zinc-300 px-2 py-1.5 text-sm w-full focus:outline-none focus:border-zinc-900"
          />
        </div>

        {error && <p className="text-red-600 text-sm">{error}</p>}

        <div>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="bg-zinc-900 text-white px-3 py-1.5 text-sm hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {mutation.isPending ? m.student_recording() : m.student_record_payment()}
          </button>
        </div>
      </form>
    </section>
  )
}

// ─── Lessons Section ──────────────────────────────────────────────────────────

function LessonsSection({ lessons }: { lessons: Lesson[] }) {
  const navigate = useNavigate()

  return (
    <section className="mb-8">
      <h2 className="text-sm font-semibold text-zinc-900 mb-3">{m.student_lessons()}</h2>
      <EntryList isEmpty={lessons.length === 0} emptyMessage={m.student_lessons_empty()}>
        {lessons.map((l) => (
          <Entry
            key={l.id}
            onClick={() => navigate({ to: '/lessons/$id', params: { id: String(l.id) } })}
            title={fd(l.scheduledAt)}
            meta={`${m.duration_min({ count: l.durationMin })} · ${l.paid ? m.student_paid() : m.student_unpaid()}`}
            status={<StatusBadge status={l.status} />}
          />
        ))}
      </EntryList>
    </section>
  )
}

// ─── Payments Section ─────────────────────────────────────────────────────────

function PaymentsSection({ payments }: { payments: Payment[] }) {
  return (
    <section className="mb-8">
      <h2 className="text-sm font-semibold text-zinc-900 mb-3">{m.student_payments()}</h2>
      <EntryList isEmpty={payments.length === 0} emptyMessage={m.student_payments_empty()}>
        {payments.map((p) => (
          <Entry
            key={p.id}
            title={`₪${Number(p.amount).toFixed(2)}`}
            meta={`${fd(p.createdAt)} · ${p.method ? (paymentMethodLabel[p.method]?.() ?? p.method) : '—'}`}
            submeta={p.note ?? undefined}
          />
        ))}
      </EntryList>
    </section>
  )
}
